import type { CallAttempt, SortOrder } from 'infrastructure/enums';

export interface IContactAvailability {
  day: number;
  startTime: string;
  endTime: string;
}

interface ActionTime {
  time: number;
  count: number;
  updatedAt: string | null;
}
interface Patient {
  guid: string;
  fullName: string;
  status: string;
  mrn: string | null;
  language: string | null;
  timezone: string;
  phone: string | null;
  monthlyApcmAssessmentDate: string | null;
  initialApcmAssessmentDate: string | null;
  apcmStatus: string | null;
  icdCodes: string[] | null;
  email: string | null;
}
interface Insurance {
  guid: string;
  provider: string;
  type: string | null;
  number: string;
}
interface Doctor {
  guid: string;
  fullName: string;
  status: string;
}
interface Clinic {
  guid: string;
  name: string;
  status: string;
  state: string | null;
  serviceDeliveryModel: string | null;
}
interface Assignee {
  loginGuid: string;
  fullName: string;
  expireAt: string;
  expireAtTimestamp: number;
  createdAt: string;
}
interface ActionTimes {
  readingReview: ActionTime;
  readingComment: ActionTime;
  readingCall: ActionTime;
  patientComment: ActionTime;
  patientCall: ActionTime;
  manual: ActionTime;
  mdReview: ActionTime;
  patientMessage: ActionTime;
  patientMessageResponse: ActionTime;
  total: number;
}

interface ReadingCount {
  normal: number;
  atRisk: number;
  critical: number;
  unreviewedAgencyReadings: number;
  unreviewedDoctorReadings: number;
}

export interface InteractiveTimeItem {
  year: number;
  month: number;
  agencyGuid: string;
  patientInteractionStatus: boolean;
  contactAvailabilities: IContactAvailability[];
  patient: Patient;
  doctor: Doctor;
  clinic: Clinic;
  insurances?: Array<Insurance | undefined>;
  actionTimes: ActionTimes;
  readingsCount: ReadingCount;
  assignee?: Assignee | null;
  createdAt: string;
  lastPhoneCall: string | null;
  lastInteractionStatusChange: string | null;
  lastInteractionStatusChangedByUserType?: string | null;
}

export interface InteractiveTimeCountData {
  interactionStatusFalseCount: number;
  interactionStatusTrueCount: number;
}

export interface InteractiveTimeDashboardData {
  count: number;
  data: InteractiveTimeItem[];
}

export interface Option {
  label: string;
  value: string;
}

export interface AvailableBillable {
  inputValue: string;
  value: number | null;
}

export interface IAvailableDay {
  value: any;
  day: number;
  name: string;
  active: boolean;
  startTime?: string;
  endTime?: string;
}

export type TInteractionFlag = 'interaction' | 'no_interaction';

export interface Filters {
  type?: 'report' | 'dashboard' | null;
  interactionFlagType?: TInteractionFlag | null;
  page: number;
  items: number;
  year: number;
  month: number;
  allExcluded?: boolean;
  withReading?: boolean;
  interactionStatus?: Option | null;
  assignee?: Option | null;
  agencyGuid?: Option[];
  clinicGuid?: Option[];
  parentClinicGuid?: Option[];
  excludeClinicGuid?: Option[];
  clinicServiceDeliveryModel?: Option[];
  clinicState?: Option[];
  language?: Option[];
  patientGuid?: Option[];
  timezone?: Option[];
  doctorGuid?: Option[];
  insurance?: Option[];
  contactAvailability?: IAvailableDay[];
  interactiveTimeRange?: Option[];
  agencyAvailableBillableMinutesMax?: AvailableBillable | null;
  agencyAvailableBillableMinutesMin?: AvailableBillable | null;
  doctorAvailableBillableMinutesMax?: AvailableBillable | null;
  doctorAvailableBillableMinutesMin?: AvailableBillable | null;
  interactionFlagStartTime?: number | null;
  interactionFlagEndTime?: number | null;
  childClinic?: boolean | string;
  status?: Option;
  ccmStatus?: Option;
  allowCriticals?: boolean;
  callAttemptsCount?: CallAttempt | null;
  sortString?: SortString | null;
  actionTimeGte?: number | null;
  actionTimeLte?: number | null;
  patientIcdCodesLengthGte?: number | null;
  patientApcmStatuses?: Option[];
  lastReadingStartTime?: number | null;
  reportType?: 'apcmEligibility' | 'interactiveTime' | null;
}

export type AppendFilters = (filter: Partial<Filters>) => void;

export const sortFields = [
  'name',
  'criticalReadings',
  'atRiskReadings',
  'normalReadings',
  'totalReadings',
  'unreviewedAgencyReadings',
  'lastPhoneCall',
  'totalInteractiveTime',
  'interactiveTimeGroup',
] as const;
export type SortField = (typeof sortFields)[number];
export type SortString = `${SortField}:${SortOrder}`;
